import React, { useCallback, useMemo, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';
import { LocalShipping, ShoppingCart } from "@mui/icons-material";
import CustomDataGrid from '../../../../components/ui/customDataGrid/customDataGrid.component';
import axios, { useApiRequest } from '../../../../api/axios';
import {
  buildOrderedPartMarkDeliveredEndpoint,
  ERROR_SNACKBAR,
  ORDERED_PARTS_ENDPOINT,
  PART_STATUS_OPTIONS,
  PART_STATUS_ORDERED,
  SUCCESS_SNACKBAR,
} from '../../../../consts';
import styles from './orderedParts.module.css';
import { useAuthContext, useHasPermission } from '../../../../contexts/auth';
import { formatMoney, formatQuantity } from '../../../../utils';
import {
  containsStringFilterOperators,
  equalsNumberFilterOperators,
  exactDateFilterOperators,
  singleSelectFilterOperators,
} from '../../../../components/ui/customDataGrid/filterOperators';

export const OrderedParts = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { executeRequest } = useApiRequest();
  const { currentUser } = useAuthContext();
  const canViewOrderedParts = useHasPermission("service.view_orderedpart");
  const canChangeOrderedParts = useHasPermission("service.change_orderedpart");
  const canChangeOwnOrderedParts = useHasPermission("service.change_own_orderedpart");
  const [busyId, setBusyId] = useState<number | null>(null);

  // The "own" permission only covers the orders the user placed - the same
  // rule the backend applies to this action.
  const canChangeOrder = (row: any) =>
    canChangeOrderedParts ||
    (canChangeOwnOrderedParts && currentUser?.id !== undefined && row.ordered_by?.id === currentUser.id);

  const markDelivered = useCallback(
    async (orderedPartId: number) => {
      setBusyId(orderedPartId);
      const response = await executeRequest(() =>
        axios.post(buildOrderedPartMarkDeliveredEndpoint(orderedPartId), {
          // The user's own today - the server runs on UTC, a day behind just
          // after midnight.
          delivered_at: dayjs().format("YYYY-MM-DD"),
        })
      );
      setBusyId(null);

      if (response?.success) {
        enqueueSnackbar(t("Order marked as delivered."), SUCCESS_SNACKBAR);
        // Delivery puts the order on the shelf, so the part's stock, price and
        // value move with it - the part page listens for this too.
        window.dispatchEvent(new Event("orderedPartUpdated"));
        return;
      }

      enqueueSnackbar(
        response?.errorData?.message ||
          response?.errorData?.detail ||
          t("The action could not be completed."),
        ERROR_SNACKBAR
      );
    },
    [enqueueSnackbar, executeRequest, t]
  );

  const STATUS_OPTIONS = useMemo(
    () => PART_STATUS_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
    [t]
  );

  const handleRowClick = (orderedPartId: string) => {
    navigate(`/service/stock-status/${id}/ordered-parts/${orderedPartId}`);
  };

  const columns: GridColDef[] = [
    {
      field: "number",
      headerName: t("Number"),
      filterOperators: containsStringFilterOperators(),
    },
    {
      field: "status",
      headerName: t("Status"),
      type: "singleSelect",
      valueOptions: STATUS_OPTIONS,
      filterOperators: singleSelectFilterOperators(),
      valueGetter: (value, row) => row.status,
      renderCell: (params) => params.row.status_display,
    },
    {
      field: "delivered_at",
      headerName: t("Delivered At"),
      editable: false,
      filterOperators: exactDateFilterOperators(),
      valueGetter: (value) => value || "-",
    },
    {
      field: "price_net",
      headerName: t("Price Net"),
      editable: false,
      filterable: false,
      valueGetter: (value, row) =>
        `${formatMoney(row.price_net)} (+${formatQuantity(row.vat)}% ${t("VAT")})`,
    },
    {
      field: "availability",
      headerName: t("Availability"),
      sortable: false,
      filterable: false,
      valueGetter: (value, row) =>
        `${formatQuantity(row.availability)} / ${formatQuantity(row.quantity)}`,
    },
    {
      field: "total",
      headerName: t("Total Cost"),
      editable: false,
      type: "number",
      filterOperators: equalsNumberFilterOperators(),
      // valueFormatter (not valueGetter) keeps the raw numeric value for
      // filtering/sorting and only changes the displayed string - unlike the
      // old valueGetter here, which broke both.
      valueFormatter: (value) => formatMoney(value),
    },
    {
      field: "ordered_by",
      headerName: t("Ordered By"),
      sortable: false,
      filterable: false,
      valueGetter: (value, row) => {
        const orderedBy = row.ordered_by;
        if (!orderedBy) return "-";
        const name = [orderedBy.first_name, orderedBy.last_name].filter(Boolean).join(' ');
        return name || orderedBy.email || "-";
      }
    },
    {
      field: "actions",
      headerName: t("Actions"),
      width: 90,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (params.row.status !== PART_STATUS_ORDERED || !canChangeOrder(params.row)) {
          return null;
        }

        const label = t("Mark as delivered");
        return (
          // Clicks and key presses stop here, so marking the order does not
          // also open it.
          <Box
            component="span"
            sx={{ display: "flex", alignItems: "center", height: "100%" }}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <Tooltip title={label}>
              {/* A disabled button fires no events, so the tooltip needs a wrapper. */}
              <span>
                <IconButton
                  size="small"
                  color="success"
                  aria-label={label}
                  disabled={busyId === params.row.id}
                  onClick={() => markDelivered(params.row.id)}
                >
                  <LocalShipping fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  if (!canViewOrderedParts) {
    return null;
  }

  const orderedPartsUrl = id ? `${ORDERED_PARTS_ENDPOINT}?part=${id}` : ORDERED_PARTS_ENDPOINT;

  return (
    <Box className={styles.container}>
      <CustomDataGrid
        icon={<ShoppingCart />}
        title={t('Ordered Parts')}
        url={orderedPartsUrl}
        gridSettingsKey="ORDERED_PARTS"
        columns={columns}
        // Using a part consumes an order's availability and can flip it to
        // "Used", so this table has to react to the used-part events too now
        // that both lists sit on the same screen.
        refreshEventName={["orderedPartAdded", "orderedPartUpdated", "usedPartAdded", "usedPartUpdated"]}
        onRowClick={handleRowClick}
      />
    </Box>
  );
};
